import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { axiosInstance } from "../auth/Auth";
import { Chart as ChartJS, Legend, Tooltip, Title } from "chart.js";
import { Chart } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useRouteLoaderData } from "react-router-dom";
import LoadingIndicator from "./LoadingIndicator";
import moment from "moment/min/moment-with-locales";
import ErrorResponse from "./ErrorResponse";
import { getRadioName, mapRotationColor } from "./HelperFunctions";
import RATooltip from "./RATooltip";
import { backgroundPlugin } from "./chartOptions";

ChartJS.register(Legend, Tooltip, ChartDataLabels, Title);

export default function CompetitorMarketTrend({ show, onHide, marketId, song, basisWeek }) {
    const { allRadioNames } = useRouteLoaderData("root");

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [radioNames, setRadioNames] = useState([]);

    const chartRef = useRef(null);

    useEffect(() => {
        async function FetchData() {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`charts/competitor/${marketId}/songs/${song.item_id}/trend?basis_week=${basisWeek}`);
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        }
        if (marketId && song) FetchData();
    }, [basisWeek, song, marketId]);

    useEffect(() => {
        async function getRadioNames() {
            setRadioNames((await allRadioNames).data);
        }
        if (allRadioNames) getRadioNames();
    }, [allRadioNames]);

    const chartOptions = {
        aspectRatio: window.innerHeight - 100 < window.innerWidth || window.innerWidth > 800 ? 2 : 1,
        borderRadius: 5,
        pointStyle: "rectRounded",
        plugins: {
            title: {
                display: true,
                text: (song?.artist + song?.title).length < 40 ? `${song?.artist} - ${song?.title}` : [song?.artist, song?.title],
                font: {
                    size: 18,
                },
                padding: {
                    bottom: 5,
                },
            },
            datalabels: {
                labels: {
                    index: {
                        padding: 0,
                        color: "black",
                        font: {
                            size: 11,
                            weight: "bold",
                        },
                    },
                },
            },
            legend: {
                display: false,
            },
            customCanvasBackgroundColor: {
                color: "white",
            },
        },
        scales: {
            y: {
                grid: {
                    display: false,
                },
            },
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: { weight: "bold" },
                },
            },
        },
        interaction: {
            mode: "index",
        },
    };

    const chartData = useMemo(() => {
        if (data) {
            const weeks = data.data[0].trend.map(x => x.basis_week);
            const labels = data.data.map(x => getRadioName(x.radio_id, radioNames));
            const datasets = weeks.map(w => ({
                type: "bar",
                label: moment(w).format("L"),
                data: data.data.map(r => {
                    const trend = r.trend.find(t => t.basis_week === w);
                    return trend ? trend.spins : 0;
                }),
                backgroundColor: data.data.map(r => mapRotationColor(r.trend.find(t => t.basis_week === w).rotation)),
            }));
            return {
                labels: labels,
                datasets: datasets,
            };
        }
    }, [data, radioNames]);

    return (
        <Modal show={show} onHide={onHide} size="xl" centered onExited={() => setData(null)} className="trend-over-time-modal">
            <Modal.Header closeButton className=" py-1 py-xl-3">
                <Modal.Title className=" text-uppercase d-flex">
                    Market Trend
                    <RATooltip className="ms-2" size={25}>
                        Weekly spins for competitors for the last 8 weeks. Colors show the rotation for each station with darker shades indicating higher
                        rotation.
                    </RATooltip>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-1 py-xl-3">
                <div>
                    {loading && <LoadingIndicator width={75} />}
                    {!loading && errorCode && <ErrorResponse errorCode={errorCode} />}
                    {data && <Chart ref={chartRef} data={chartData} options={chartOptions} plugins={[backgroundPlugin]} />}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export function useCompetitorMarketTrendModal() {
    const [trendSong, setTrendSong] = useState(null);

    const CompetitorMarketTrendModalComponent = ({ marketId, basisWeek }) => (
        <CompetitorMarketTrend show={!!trendSong} onHide={() => setTrendSong(null)} song={trendSong} marketId={marketId} basisWeek={basisWeek} />
    );

    return { setTrendSong, CompetitorMarketTrendModalComponent };
}
